import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LogOut, Download, Users, Target, Volleyball, UserX, Trash2, Mail, Settings as SettingsIcon, RefreshCw, Plus, X,
} from "lucide-react";
import { api, formatApiError, activityLabel } from "@/lib/api";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("admin_token", data.token);
      onLogin();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#050505] px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0f0f13] p-8" data-testid="admin-login">
        <h1 className="font-display text-2xl font-black tracking-tight">Area Amministratore</h1>
        <p className="mt-1 text-sm text-white/50">Convention Rea Company Srl</p>
        <div className="mt-6 space-y-4">
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="w-full rounded-xl border border-white/10 bg-[#050505] px-4 py-3 text-sm outline-none focus:border-[#f5c542]" data-testid="admin-email" />
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="w-full rounded-xl border border-white/10 bg-[#050505] px-4 py-3 text-sm outline-none focus:border-[#f5c542]" data-testid="admin-password" />
          <button type="submit" disabled={loading}
            className="btn-neon w-full rounded-xl py-3 font-display text-sm font-extrabold uppercase tracking-wider disabled:opacity-60" data-testid="admin-login-btn">
            {loading ? "Accesso..." : "Accedi"}
          </button>
        </div>
      </form>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="rounded-xl border border-white/10 bg-[#0f0f13] p-5">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-white/40">{label}</span>
      <Icon size={18} style={{ color: accent }} />
    </div>
    <div className="mt-3 font-display text-3xl font-black">{value}</div>
    {sub && <div className="mt-1 text-xs text-white/40">{sub}</div>}
  </div>
);

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [regs, setRegs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("registrazioni");
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/registrations").then((r) => setRegs(r.data));
    api.get("/admin/notifications").then((r) => setNotifs(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "partecipanti_convention.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export non riuscito");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa registrazione?")) return;
    await api.delete(`/admin/registrations/${id}`);
    toast.success("Registrazione eliminata");
    load();
  };

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Impostazioni salvate");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setProgram = (i, k, v) =>
    setSettings((s) => ({ ...s, program: s.program.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) }));
  const addProgram = () => setSettings((s) => ({ ...s, program: [...s.program, { time: "", title: "" }] }));
  const rmProgram = (i) => setSettings((s) => ({ ...s, program: s.program.filter((_, idx) => idx !== i) }));

  const setWinner = (i, k, v) =>
    setSettings((s) => ({ ...s, winners: (s.winners || []).map((wn, idx) => (idx === i ? { ...wn, [k]: v } : wn)) }));
  const addWinner = () => setSettings((s) => ({ ...s, winners: [...(s.winners || []), { category: "", name: "", image: "", year: "2025" }] }));
  const rmWinner = (i) => setSettings((s) => ({ ...s, winners: (s.winners || []).filter((_, idx) => idx !== i) }));

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#050505]/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="font-display text-lg font-black tracking-tight">Dashboard Convention</h1>
            <p className="text-xs text-white/40">Rea Company Srl · Fastweb + Vodafone</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="rounded-lg border border-white/15 p-2 text-white/70 hover:bg-white/5" data-testid="refresh-btn"><RefreshCw size={16} /></button>
            <button onClick={exportCsv} className="flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm hover:bg-white/5" data-testid="export-btn"><Download size={15} /> CSV</button>
            <button onClick={onLogout} className="flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm hover:bg-white/5" data-testid="logout-btn"><LogOut size={15} /> Esci</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={Users} label="Partecipanti" value={stats?.total ?? "—"} accent="#f5c542" />
          <StatCard icon={Target} label="Iscritti Padel" value={stats?.padel ?? "—"} sub={`${stats?.padel_full ? "ESAURITO" : "su " + stats?.padel_capacity} posti`} accent="#e60000" />
          <StatCard icon={Volleyball} label="Iscritti Beach" value={stats?.beach ?? "—"} sub={`${stats?.beach_full ? "ESAURITO" : "su " + stats?.beach_capacity} posti`} accent="#e60000" />
          <StatCard icon={UserX} label="Non parteciperanno" value={stats?.declined ?? "—"} accent="#e60000" />
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-2 border-b border-white/10">
          {[["registrazioni", "Registrazioni"], ["notifiche", "Notifiche"], ["impostazioni", "Impostazioni"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-2 text-sm font-medium transition-colors ${tab === k ? "border-b-2 border-[#f5c542] text-white" : "text-white/40 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "registrazioni" && (
          <div className="mt-6 overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-sm" data-testid="registrations-table">
              <thead className="bg-[#0f0f13] text-xs uppercase tracking-wider text-white/40">
                <tr>
                  <th className="px-4 py-3">Nome</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Azienda</th>
                  <th className="px-4 py-3">Presenza</th>
                  <th className="px-4 py-3">Torneo</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {regs.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">Nessuna registrazione</td></tr>
                )}
                {regs.map((r) => (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="cursor-pointer px-4 py-3 font-medium" onClick={() => setSelected(r)}>{r.first_name} {r.last_name}</td>
                    <td className="px-4 py-3 text-white/60">{r.email}</td>
                    <td className="px-4 py-3 text-white/60">{r.company}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${r.attending === "yes" ? "bg-[#f5c542]/15 text-[#f5c542]" : "bg-[#e60000]/15 text-[#ff6b6b]"}`}>
                        {r.attending === "yes" ? "Sì" : "No"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/60">{activityLabel[r.tournament] || "-"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => del(r.id)} className="text-white/40 hover:text-[#ff6b6b]" data-testid={`del-${r.id}`}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "notifiche" && (
          <div className="mt-6 space-y-3">
            {notifs.length === 0 && <p className="text-white/40">Nessuna notifica</p>}
            {notifs.map((n) => (
              <div key={n.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0f0f13] p-4 text-sm">
                <Mail size={16} className="text-[#f5c542]" />
                <span>{n.message}</span>
                <span className="ml-auto text-xs text-white/30">{new Date(n.created_at).toLocaleString("it-IT")}</span>
              </div>
            ))}
          </div>
        )}

        {tab === "impostazioni" && settings && (
          <div className="mt-6 space-y-6">
            <div className="grid gap-4 rounded-xl border border-white/10 bg-[#0f0f13] p-6 sm:grid-cols-2">
              {[
                ["event_date", "Data (testo)"], ["event_date_iso", "Data ISO (countdown)"], ["event_time", "Orario"],
                ["location", "Location"], ["city", "Città"], ["video_url", "URL Video intro"],
                ["logo_rea", "URL Logo Rea"], ["logo_partners", "URL Logo Partner"],
                ["org_contact", "Contatto org."], ["org_email", "Email org."], ["org_phone", "Telefono org."],
              ].map(([k, l]) => (
                <label key={k} className="text-sm">
                  <span className="mb-1 block text-xs uppercase tracking-wider text-white/40">{l}</span>
                  <input value={settings[k] || ""} onChange={(e) => setS(k, e.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-[#050505] px-3 py-2 outline-none focus:border-[#f5c542]" data-testid={`set-${k}`} />
                </label>
              ))}
              <label className="text-sm">
                <span className="mb-1 block text-xs uppercase tracking-wider text-white/40">Capienza Padel</span>
                <input type="number" value={settings.padel_capacity} onChange={(e) => setS("padel_capacity", Number(e.target.value))}
                  className="w-full rounded-lg border border-white/10 bg-[#050505] px-3 py-2 outline-none focus:border-[#f5c542]" data-testid="set-padel-cap" />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-xs uppercase tracking-wider text-white/40">Capienza Beach</span>
                <input type="number" value={settings.beach_capacity} onChange={(e) => setS("beach_capacity", Number(e.target.value))}
                  className="w-full rounded-lg border border-white/10 bg-[#050505] px-3 py-2 outline-none focus:border-[#f5c542]" data-testid="set-beach-cap" />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block text-xs uppercase tracking-wider text-white/40">Sottotitolo Hero</span>
                <textarea rows={2} value={settings.hero_subtitle || ""} onChange={(e) => setS("hero_subtitle", e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-[#050505] px-3 py-2 outline-none focus:border-[#f5c542]" />
              </label>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0f0f13] p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-display font-bold">Programma</h3>
                <button onClick={addProgram} className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5"><Plus size={14} /> Voce</button>
              </div>
              <div className="space-y-2">
                {settings.program?.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={p.time} onChange={(e) => setProgram(i, "time", e.target.value)} placeholder="Orario"
                      className="w-28 rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm outline-none focus:border-[#f5c542]" />
                    <input value={p.title} onChange={(e) => setProgram(i, "title", e.target.value)} placeholder="Titolo"
                      className="flex-1 rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm outline-none focus:border-[#f5c542]" />
                    <button onClick={() => rmProgram(i)} className="text-white/40 hover:text-[#ff6b6b]"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0f0f13] p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-display font-bold">Albo d'Oro (Vincitori)</h3>
                <button onClick={addWinner} className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5"><Plus size={14} /> Vincitore</button>
              </div>
              <div className="space-y-2">
                {(settings.winners || []).map((wn, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <input value={wn.category} onChange={(e) => setWinner(i, "category", e.target.value)} placeholder="Categoria"
                      className="w-40 rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm outline-none focus:border-[#f5c542]" />
                    <input value={wn.name} onChange={(e) => setWinner(i, "name", e.target.value)} placeholder="Nome vincitore/i"
                      className="min-w-[160px] flex-1 rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm outline-none focus:border-[#f5c542]" />
                    <input value={wn.year} onChange={(e) => setWinner(i, "year", e.target.value)} placeholder="Anno"
                      className="w-20 rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm outline-none focus:border-[#f5c542]" />
                    <input value={wn.image} onChange={(e) => setWinner(i, "image", e.target.value)} placeholder="URL foto"
                      className="min-w-[180px] flex-1 rounded-lg border border-white/10 bg-[#050505] px-3 py-2 text-sm outline-none focus:border-[#f5c542]" />
                    <button onClick={() => rmWinner(i)} className="text-white/40 hover:text-[#ff6b6b]"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={saveSettings} className="btn-neon rounded-xl px-8 py-3 font-display text-sm font-extrabold uppercase tracking-wider" data-testid="save-settings-btn">
              <SettingsIcon size={16} className="mr-2 inline" /> Salva impostazioni
            </button>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => setSelected(null)}>
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#0f0f13] p-6" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">{selected.first_name} {selected.last_name}</h3>
              <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <dl className="space-y-2 text-sm">
              {[
                ["Email", selected.email], ["Telefono", selected.phone], ["Azienda", selected.company],
                ["Ruolo", selected.role], ["Città", selected.city], ["Presenza", selected.attending],
                ["Accompagnatori", selected.companions], ["Torneo", activityLabel[selected.tournament]],
                ["Livello Padel", selected.padel_level || "-"], ["Livello Beach", selected.beach_level || "-"],
                ["Disponibile in squadra", selected.open_to_team ? "Sì" : "No"], ["Compagni", selected.team_members || "-"],
                ["Dieta", selected.diet], ["Note dieta", selected.diet_notes || "-"], ["Maglietta", selected.tshirt || "-"],
                ["Consenso privacy", selected.consent_privacy ? "Sì" : "No"],
                ["Consenso media", selected.consent_media ? "Sì" : "No"],
                ["Consenso comunicazioni", selected.consent_comms ? "Sì" : "No"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-white/5 py-1.5">
                  <dt className="text-white/40">{k}</dt><dd className="text-right font-medium">{String(v)}</dd>
                </div>
              ))}
            </dl>
            {selected.email_log && (
              <div className="mt-4 rounded-lg border border-white/10 bg-[#050505] p-4">
                <p className="mb-2 text-xs uppercase tracking-wider text-[#f5c542]">Email di conferma (anteprima)</p>
                <p className="text-xs font-bold text-white/70">{selected.email_log.subject}</p>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-white/50">{selected.email_log.body}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => localStorage.removeItem("admin_token")).finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem("admin_token");
    setAuthed(false);
  };

  if (checking) return <div className="grid min-h-screen place-items-center bg-[#050505]"><div className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-[#f5c542]" /></div>;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;
  return <Dashboard onLogout={logout} />;
}
